/**
 * FinBadge — renders the Fridge.com Identification Number (FIN) on a PDP.
 *
 * Renders FIN-0042893 monospace below H1, links to the /about-us/fin explainer.
 *
 * The FIN is the canonical anchor for every product in the Product Intelligence
 * Layer — it stays stable forever, even if model normalization improves later.
 * It powers URL slugs (/products/{brand}-{type}-{model}-fin{NNNNNNN}), Keepa
 * price-history linkage, CPSC recall matching, and the /api/products/[fin]/json
 * public endpoint AI agents will cite.
 *
 * Display contract:
 *   - Monospace font (it's an identifier)
 *   - Uppercase with hyphen ("FIN-0042893") — distinct from URL slug form ("fin0042893")
 *   - Click-through to /about-us/fin so first-time visitors learn what it is
 *   - Quiet visual weight — informational, not promotional
 */

import Link from 'next/link';

interface FinBadgeProps {
  fin: string | null | undefined;
  className?: string;
}

export function FinBadge({ fin, className = '' }: FinBadgeProps) {
  if (!fin) return null;
  return (
    <Link
      href="/about-us/fin"
      className={`inline-flex items-center gap-1.5 text-xs font-mono text-gray-600 hover:text-[#0469f0] transition-colors ${className}`}
      aria-label={`Fridge.com Identification Number ${fin} — learn more about FINs`}
      title="Fridge.com Identification Number (FIN) — click to learn more"
    >
      <span className="px-1.5 py-0.5 bg-gray-100 rounded border border-gray-200 group-hover:border-[#0469f0]/40 tracking-wider">
        {fin}
      </span>
    </Link>
  );
}

export default FinBadge;
